import { useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Trophy, Users, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function JoinLeaguePage() {
  const [, setLocation] = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { toast } = useToast();

  const path = window.location.pathname;
  const inviteCode = path.split("/join/")[1];

  useEffect(() => {
    if (inviteCode && isAuthenticated) {
      localStorage.removeItem('pendingInviteCode');
      localStorage.removeItem('pendingInviteTimestamp');
      document.cookie = 'pendingInvite=; Max-Age=0; path=/';
    } else if (inviteCode && !authLoading && !isAuthenticated) {
      localStorage.setItem('pendingInviteCode', inviteCode);
      localStorage.setItem('pendingInviteTimestamp', Date.now().toString());
    }
  }, [inviteCode, isAuthenticated, authLoading]);

  const {
    data: leagueInfo,
    isLoading,
    error,
    refetch,
  } = useQuery<{
    id: string;
    name: string;
    description: string | null;
    matchFormat: string;
    isPrivate: boolean;
  }>({
    queryKey: ["/api/leagues/join", inviteCode],
    enabled: !!inviteCode,
    retry: 1,
  });

  const joinMutation = useMutation({
    mutationFn: async () => {
      if (!leagueInfo?.id) throw new Error("Liga no encontrada");
      const res = await fetch(`/api/leagues/${leagueInfo.id}/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ inviteCode }),
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        const err = new Error(data.message || "No se pudo unir a la liga") as Error & { code?: string; leagueId?: string };
        if (data.code) (err as any).code = data.code;
        if (data.leagueId) (err as any).leagueId = data.leagueId;
        throw err;
      }
      return data;
    },
    onSuccess: () => {
      localStorage.removeItem('pendingInviteCode');
      localStorage.removeItem('pendingInviteTimestamp');
      document.cookie = 'pendingInvite=; Max-Age=0; path=/';
      queryClient.invalidateQueries({ queryKey: ["/api/leagues"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leagues", leagueInfo?.id] });
      toast({ title: "¡Te has unido!", description: `Bienvenido a ${leagueInfo?.name}` });
      setLocation("/");
    },
    onError: (error: Error & { code?: string; leagueId?: string }) => {
      if (error.code === "ALREADY_MEMBER") {
        localStorage.removeItem('pendingInviteCode');
        localStorage.removeItem('pendingInviteTimestamp');
        document.cookie = 'pendingInvite=; Max-Age=0; path=/';
        toast({ title: "Ya eres miembro", description: `Ya perteneces a ${leagueInfo?.name}` });
        setLocation("/");
        return;
      }
      toast({
        title: "Error al unirse",
        description: error.message || "No se pudo unir a la liga",
        variant: "destructive",
      });
    },
  });

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Trophy className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Cargando invitación...</p>
        </div>
      </div>
    );
  }

  if (!inviteCode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
            <h2 className="text-xl font-bold mb-2" data-testid="text-invalid-link">Link inválido</h2>
            <p className="text-muted-foreground mb-4">
              El enlace de invitación no contiene un código válido.
            </p>
            <Button onClick={() => setLocation("/")} data-testid="button-go-home">
              Ir al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !leagueInfo) {
    const is404 = (error as Error)?.message?.includes("404");
    const isNetwork =
      (error as Error)?.message?.includes("Failed to fetch") ||
      (error as Error)?.message?.includes("NetworkError");
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
            <h2 className="text-xl font-bold mb-2" data-testid="text-error-title">
              {is404 ? "Liga no encontrada" : isNetwork ? "Error de conexión" : "No se pudo cargar"}
            </h2>
            <p className="text-muted-foreground mb-4">
              {is404
                ? "Este código de invitación no existe o la liga fue eliminada."
                : isNetwork
                  ? "Verifica tu conexión a internet e intenta de nuevo."
                  : "Hubo un problema al cargar la invitación. Intenta de nuevo."}
            </p>
            <div className="flex gap-2 justify-center">
              {!is404 && (
                <Button variant="outline" onClick={() => refetch()} data-testid="button-retry">
                  Reintentar
                </Button>
              )}
              <Button onClick={() => setLocation("/")} data-testid="button-go-home">
                Ir al inicio
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Trophy className="h-12 w-12 text-primary mx-auto mb-2" />
            <CardTitle>Invitación a Liga</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-lg font-semibold" data-testid="text-league-name">{leagueInfo.name}</p>
              {leagueInfo.description && (
                <p className="text-sm text-muted-foreground mt-1">{leagueInfo.description}</p>
              )}
            </div>
            <p className="text-muted-foreground">Inicia sesión para unirte a esta liga</p>
            <Button
              onClick={() =>
                (window.location.href = `/api/login/player?returnTo=${encodeURIComponent(window.location.pathname)}`)
              }
              className="w-full"
              data-testid="button-login-to-join"
            >
              <LogIn className="h-4 w-4 mr-2" />
              Iniciar Sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Trophy className="h-12 w-12 text-primary mx-auto mb-2" />
          <CardTitle>Unirse a Liga</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-lg font-semibold" data-testid="text-league-name">{leagueInfo.name}</p>
            {leagueInfo.description && (
              <p className="text-sm text-muted-foreground mt-1">{leagueInfo.description}</p>
            )}
            <p className="text-sm text-muted-foreground mt-2">
              {leagueInfo.matchFormat === "doubles" ? "Formato: Dobles" : "Formato: Individual"}
            </p>
          </div>
          <p className="text-muted-foreground">¿Quieres unirte a esta liga?</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setLocation("/")}
              data-testid="button-cancel-join"
            >
              Cancelar
            </Button>
            <Button
              className="flex-1"
              onClick={() => joinMutation.mutate()}
              disabled={joinMutation.isPending}
              data-testid="button-confirm-join"
            >
              {joinMutation.isPending ? "Uniendo..." : "Unirme"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
